import * as Sentry from '@sentry/browser';
import { find } from 'lodash';
import { action, computed, makeObservable, observable } from 'mobx';
import moment from 'moment';

import { ComponentTierName, CurrencySymbol, InsightStatus, InsightType } from '@/constants/enums';
import { compressorTypes } from '@/constants/deviceTypeEnum';
import { checkTierPermission, isExposedEvent } from '@/utils/util-functions';
import epaxios from '@/utils/epaxios';

import RootStore from './RootStore';
import { emptyValue } from './LeakageStore';

export default class InsightsStore {
  rootStore: typeof RootStore;
  @observable overview: Overview;
  @observable events: InsightEvent[] = [];
  @observable pendingTasksOnly = false;
  @observable eventsLimit?: number;
  @observable isLoading = false;
  @observable isLoadingEvent = false;
  @observable isUpdatingStatus = false;
  @observable isLoadingAvailableInsights = false;
  @observable availableInsights: any[] = []; // Raw insights for assistant (has 'id' property)

  constructor(rootStore: typeof RootStore) {
    makeObservable(this);
    this.rootStore = rootStore;
  }

  @action getOverview = async () => {
    this.isLoading = true;
    try {
      const stationId = this.rootStore.stationStore?.currentStation.id;
      const { data } = await epaxios.get(`value-dashboard/insights/station/${stationId}/overview`);
      this.overview = new Overview(data);
    } catch (e) {
      Sentry.captureException(new Error(`Error when getting Insights Overview: ${e}`));
    } finally {
      this.isLoading = false;
    }
  };

  @action getAssistantInsights = async () => {
    this.isLoadingAvailableInsights = true;
    try {
      const stationId = this.rootStore.stationStore?.currentStation.id;
      const { data } = await epaxios.get(`value-dashboard/insights/assistant/station/${stationId}`, {});
      this.availableInsights = data || [];
    } catch (e) {
      Sentry.captureException(new Error(`Error when getting Available Insights: ${e}`));
    } finally {
      this.isLoadingAvailableInsights = false;
    }
  };

  @action getEvents = async () => {
    this.isLoading = true;
    try {
      this.events = [];
      const stationId = this.rootStore.stationStore?.currentStation.id;
      let { data } = await epaxios.get(`value-dashboard/insights/station/${stationId}/filtered-records`, {
        params: { pendingTasksOnly: this.pendingTasksOnly },
      });
      if (!data) {
        Sentry.captureMessage('No data received from the API', 'warning');
        return;
      }

      data = data.slice(0, this.eventsLimit ?? data.length);
      this.events = data.map(event => new InsightEvent(event, this.rootStore));
    } catch (e) {
      Sentry.captureException(new Error(`Error when getting Insights Events: ${e}`));
    } finally {
      this.isLoading = false;
    }
  };

  @computed get filteredEvents() {
    return this.events?.filter(event => !isExposedEvent(event, this.rootStore.stationStore));
  }

  @computed get filteredOutEvents() {
    return this.events?.filter(event => isExposedEvent(event, this.rootStore.stationStore));
  }

  @computed get availableInsightsList() {
    if (!this.availableInsights || this.availableInsights.length === 0) return [];

    return this.availableInsights.filter((insight: any) => {
      if (!insight.status) return true;
      const status = insight.status.toString().toLowerCase();
      return status === 'open' || status === 'draft';
    });
  }

  @computed get archivedInsightsList() {
    if (!this.availableInsights || this.availableInsights.length === 0) return [];

    return this.availableInsights.filter((insight: any) => {
      if (!insight.status) return false;
      const status = insight.status.toString().toLowerCase();
      return status === 'archived';
    });
  }

  @computed get publishedInsightsList() {
    if (!this.availableInsights || this.availableInsights.length === 0) return [];

    return this.availableInsights.filter((insight: any) => {
      if (!insight.status) return false;
      const status = insight.status.toString().toLowerCase();
      return status === 'open';
    });
  }

  @computed get draftInsightsList() {
    if (!this.availableInsights || this.availableInsights.length === 0) return [];

    return this.availableInsights.filter((insight: any) => {
      if (!insight.status) return true; // If no status, treat as draft
      const status = insight.status.toString().toLowerCase();
      return status === 'draft';
    });
  }

  @action getEvent = async (eventId: string): Promise<InsightEvent | null> => {
    this.isLoadingEvent = true;
    try {
      const stationId = this.rootStore.stationStore?.currentStation.id;
      const { data } = await epaxios.get(`value-dashboard/insights/station/${stationId}/records/${eventId}`);
      return new InsightEvent(data, this.rootStore);
    } catch (e) {
      Sentry.captureException(new Error(`Error when getting Insights Event: ${e}`));
      return null;
    } finally {
      this.isLoadingEvent = false;
    }
  };

  @action setPendingTasksOnly = (pendingTasksOnly: boolean) => {
    this.pendingTasksOnly = pendingTasksOnly;
  };

  @action setEventsLimit = (eventsLimit: number) => {
    this.eventsLimit = eventsLimit;
  };

  @action updateInsightStatus = async (insightEventId: string, status: InsightStatus) => {
    this.isUpdatingStatus = true;
    try {
      const stationId = this.rootStore.stationStore?.currentStation.id;
      if (insightEventId) {
        await epaxios.put(`value-dashboard/insights/station/${stationId}/records/${insightEventId}/status`, {
          status,
        });
        this.getAssistantInsights();
        this.getEvents();
      }
    } catch (e) {
      Sentry.captureException(new Error(`Error when updating Insight Event status: ${e}`));
    } finally {
      this.isUpdatingStatus = false;
    }
  };

  @action updateInsight = async (
    insightEventId: string,
    updateData: {
      name?: string;
      deviceId?: string;
      description?: string;
      monetizedAnnualValue?: number;
      currency?: string;
      valueType?: InsightType;
      status?: InsightStatus;
    },
  ) => {
    this.isUpdatingStatus = true;
    try {
      const stationId = this.rootStore.stationStore?.currentStation.id;
      if (insightEventId) {
        await epaxios.put(`value-dashboard/insights/station/${stationId}/records/${insightEventId}`, updateData);
        this.getAssistantInsights();
        this.getEvents();
      }
    } catch (e) {
      Sentry.captureException(new Error(`Error when updating Insight Event: ${e}`));
    } finally {
      this.isUpdatingStatus = false;
    }
  };

  @action updateAssistantInsight = async (
    insightEventId: string,
    updateData: {
      name?: string;
      deviceId?: string;
      description?: string;
      monetizedAnnualValue?: number;
      currency?: string;
      valueType?: InsightType;
      status?: InsightStatus;
    },
  ) => {
    this.isUpdatingStatus = true;
    try {
      const stationId = this.rootStore.stationStore?.currentStation.id;
      if (insightEventId) {
        await epaxios.put(
          `value-dashboard/insights/assistant/station/${stationId}/records/${insightEventId}`,
          updateData,
        );
        this.getAssistantInsights();
        this.getEvents();
        this.getOverview();
      }
    } catch (e) {
      Sentry.captureException(new Error(`Error when updating Assistant Insight Event: ${e}`));
    } finally {
      this.isUpdatingStatus = false;
    }
  };
}

class Overview {
  @observable total: {
    insightsCount: number;
    totalValue: number;
    currency: string;
    risk: ActionBreakdown;
    maintenance: ActionBreakdown;
    energy: ActionBreakdown;
    rejectedAmount: number;
  };
  @observable actionsTaken: ActionInfo;
  @observable actionsPending: ActionInfo;

  constructor(overview) {
    makeObservable(this);
    const lifetime = overview?.lifetime;
    this.total = {
      insightsCount: lifetime?.total?.count - (lifetime?.total?.rejectedAmount ?? 0),
      totalValue: lifetime?.total?.value,
      currency: lifetime?.total?.currency,
      rejectedAmount: lifetime?.total?.rejectedAmount,
      risk: new ActionBreakdown(lifetime?.total?.risk),
      maintenance: new ActionBreakdown(lifetime?.total?.maintenance),
      energy: new ActionBreakdown(lifetime?.total?.energy),
    };
    this.actionsTaken = new ActionInfo(lifetime?.actionsTaken);
    this.actionsPending = new ActionInfo(lifetime?.actionsPending);
  }
}

export class ActionBreakdown {
  @observable actionsTaken: number;
  @observable actionsPending: number;
  @observable total: number;

  constructor(actionBreakdown) {
    makeObservable(this);
    this.actionsTaken = actionBreakdown.actionsTaken;
    this.actionsPending = actionBreakdown.actionsPending;
    this.total = actionBreakdown.total;
  }
}

export class ActionInfo {
  @observable count: number;
  @observable value: number;
  @observable currency: string;

  constructor(actionInfo) {
    makeObservable(this);
    this.count = actionInfo.count;
    this.value = actionInfo.value;
    this.currency = actionInfo.currency;
  }
}

export class InsightEvent {
  rootStore: typeof RootStore;
  @observable creationTime: Date;
  @observable dateUpdated: Date;
  @observable insightName: string;
  @observable assetName: string;
  @observable description: string;
  @observable insightType: InsightType;
  @observable value: number;
  @observable currency: string;
  @observable status: InsightStatus;
  @observable attachments: InsightAttachment[];
  @observable key: string;
  @observable deviceId: string | null;

  constructor(event, rootStore) {
    makeObservable(this);
    this.rootStore = rootStore;
    this.key = event.id;
    this.creationTime = event.dateCreated;
    this.dateUpdated = event.dateUpdated;
    this.insightName = event.name;
    this.deviceId = event.deviceId || null;
    this.assetName = this.getAssetName(event.deviceId, event.stationId);
    this.description = event.description;
    this.insightType = event.valueType;
    this.value = event.monetizedAnnualValue;
    this.currency = event.currency ?? CurrencySymbol.USD;
    this.status = event.status;
    this.attachments = event.attachments?.map(attachment => new InsightAttachment(attachment));
  }

  @action setAttachments(attachments: InsightAttachment[]) {
    this.attachments = attachments;
  }

  private getAssetName(deviceId: string, stationId: string): string {
    const shouldRestrictInsights = checkTierPermission(ComponentTierName.Insights, this.rootStore.stationStore);
    const device = this.rootStore.devicesStore.getDeviceById(deviceId);
    const isCompressor = device?.deviceTypes.some(type => compressorTypes.includes(type));
    if (shouldRestrictInsights && !isCompressor) return 'system';
    return deviceId
      ? find(this.rootStore.devicesStore.devicesMetadata, { id: deviceId })?.name
      : find(this.rootStore.stationStore.stations, { id: stationId })?.name;
  }

  @computed get creationTimeFormatted() {
    return this.creationTime ? moment(this.creationTime).format('ll') : emptyValue;
  }

  @computed get dateUpdatedFormatted() {
    return this.dateUpdated ? moment(this.dateUpdated).format('ll') : emptyValue;
  }
}

export class InsightAttachment {
  @observable id: string;
  @observable title: string;
  @observable url: string;

  constructor(attachment) {
    makeObservable(this);
    this.id = attachment.id;
    this.title = attachment.title;
    this.url = attachment.url;
  }
}
